import api from '@/helpers/api_helper';
import { apiUrls } from '@/constant/configure';
import type {
    CancelSubscriptionReq,
    ChangePlanPreviewDto,
    ChangePlanReq,
    NicePayAuthParams,
    PaymentMethodDto,
    RegisterPaymentMethodInitReq,
    RegisterPaymentMethodReq,
    SubscribeReq,
    SubscriptionChangeHistoryDto,
    SubscriptionDto,
    SubscriptionFeatureDto,
    SubscriptionPaymentDto,
    SubscriptionPlanDto,
    SubscriptionSnapshotDto,
    SubscriptionUsageDto,
} from '@/types/subscription';

const mall = { baseURL: apiUrls.mall };
const PREFIX = '/api/v1/subscription';

// ============================================
// Public
// ============================================

export const getSubscriptionPlans = async (): Promise<SubscriptionPlanDto[]> => {
    const res = await api.get<{ itemList: SubscriptionPlanDto[] }>(`${PREFIX}/plans`, mall);
    return res.data.itemList;
};

export const getSubscriptionPlan = async (code: string): Promise<SubscriptionPlanDto> => {
    const res = await api.get<{ item: SubscriptionPlanDto }>(`${PREFIX}/plans/${code}`, mall);
    return res.data.item;
};

export const getSubscriptionFeatures = async (): Promise<SubscriptionFeatureDto[]> => {
    const res = await api.get<{ itemList: SubscriptionFeatureDto[] }>(`${PREFIX}/features`, mall);
    return res.data.itemList;
};

// ============================================
// 결제 수단 (NicePay 빌링키)
// ============================================

export const initPaymentMethodRegistration = async (
    body: RegisterPaymentMethodInitReq,
): Promise<NicePayAuthParams> => {
    const res = await api.post<{ item: NicePayAuthParams }>(
        `${PREFIX}/payment-methods/register-init`,
        body,
        mall,
    );
    return res.data.item;
};

export const registerPaymentMethod = async (
    body: RegisterPaymentMethodReq,
): Promise<PaymentMethodDto> => {
    const res = await api.post<{ item: PaymentMethodDto }>(`${PREFIX}/payment-methods`, body, mall);
    return res.data.item;
};

export const getPaymentMethods = async (): Promise<PaymentMethodDto[]> => {
    const res = await api.get<{ itemList: PaymentMethodDto[] }>(`${PREFIX}/payment-methods`, mall);
    return res.data.itemList;
};

export const setDefaultPaymentMethod = async (id: string): Promise<PaymentMethodDto> => {
    const res = await api.patch<{ item: PaymentMethodDto }>(
        `${PREFIX}/payment-methods/${id}/default`,
        {},
        mall,
    );
    return res.data.item;
};

export const deletePaymentMethod = async (id: string): Promise<PaymentMethodDto> => {
    const res = await api.delete<{ item: PaymentMethodDto }>(`${PREFIX}/payment-methods/${id}`, mall);
    return res.data.item;
};

// ============================================
// 구독
// ============================================

export const getMySubscription = async (): Promise<SubscriptionDto | null> => {
    const res = await api.get<{ item: SubscriptionDto | null }>(`${PREFIX}/me`, mall);
    return res.data.item;
};

export const subscribe = async (body: SubscribeReq): Promise<SubscriptionDto> => {
    const res = await api.post<{ item: SubscriptionDto }>(PREFIX, body, mall);
    return res.data.item;
};

export const changePlan = async (body: ChangePlanReq): Promise<SubscriptionDto> => {
    const res = await api.patch<{ item: SubscriptionDto }>(`${PREFIX}/plan`, body, mall);
    return res.data.item;
};

export const previewChangePlan = async (
    planCode: string,
    billingCycle?: 'monthly' | 'yearly',
): Promise<ChangePlanPreviewDto> => {
    const res = await api.get<{ item: ChangePlanPreviewDto }>(`${PREFIX}/plan/preview`, {
        ...mall,
        params: { planCode, billingCycle },
    });
    return res.data.item;
};

export const cancelScheduledPlanChange = async (): Promise<SubscriptionDto> => {
    const res = await api.post<{ item: SubscriptionDto }>(`${PREFIX}/plan/cancel-scheduled`, {}, mall);
    return res.data.item;
};

export const cancelSubscription = async (body: CancelSubscriptionReq): Promise<SubscriptionDto> => {
    const res = await api.post<{ item: SubscriptionDto }>(`${PREFIX}/cancel`, body, mall);
    return res.data.item;
};

export const resumeSubscription = async (): Promise<SubscriptionDto> => {
    const res = await api.post<{ item: SubscriptionDto }>(`${PREFIX}/resume`, {}, mall);
    return res.data.item;
};

export const getMySubscriptionSnapshot = async (): Promise<SubscriptionSnapshotDto> => {
    const res = await api.get<{ item: SubscriptionSnapshotDto }>(`${PREFIX}/me/snapshot`, mall);
    return res.data.item;
};

export const getSubscriptionChangeHistory = async (): Promise<SubscriptionChangeHistoryDto[]> => {
    const res = await api.get<{ itemList: SubscriptionChangeHistoryDto[] }>(
        `${PREFIX}/me/change-history`,
        mall,
    );
    return res.data.itemList;
};

// ============================================
// 결제 내역 & 환불
// ============================================

export const getPayments = async (): Promise<SubscriptionPaymentDto[]> => {
    const res = await api.get<{ itemList: SubscriptionPaymentDto[] }>(`${PREFIX}/payments`, mall);
    return res.data.itemList;
};

export const getPayment = async (id: string): Promise<SubscriptionPaymentDto> => {
    const res = await api.get<{ item: SubscriptionPaymentDto }>(`${PREFIX}/payments/${id}`, mall);
    return res.data.item;
};

export const refundPayment = async (id: string): Promise<SubscriptionPaymentDto> => {
    const res = await api.post<{ item: SubscriptionPaymentDto }>(
        `${PREFIX}/payments/${id}/refund`,
        {},
        mall,
    );
    return res.data.item;
};

// ============================================
// 사용량
// ============================================

export const getUsage = async (): Promise<SubscriptionUsageDto[]> => {
    const res = await api.get<{ itemList: SubscriptionUsageDto[] }>(`${PREFIX}/usage`, mall);
    return res.data.itemList;
};

export const getFeatureUsage = async (featureCode: string): Promise<SubscriptionUsageDto | null> => {
    const res = await api.get<{ item: SubscriptionUsageDto | null }>(
        `${PREFIX}/usage/${featureCode}`,
        mall,
    );
    return res.data.item;
};
